'use client';
import React, { useState } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import AppDownloadModal from '@/components/AppDownloadModal';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';
import { createClient } from '@/lib/supabase/client';

const FAQ_CATEGORIES = [
  {
    id: 'account',
    icon: 'UserCircleIcon',
    color: '#FF5722',
    bg: '#FFF3EE',
    title: 'Account & Profile',
    faqs: [
      { q: 'How do I reset my password?', a: 'Go to the login page and click "Forgot Password". Enter your registered email and we\'ll send a reset link within a few minutes. Check your spam folder if you don\'t see it.' },
      { q: 'How do I update my UPI ID for payouts?', a: 'Visit your reviewer dashboard → Settings → Payout Details. Update your UPI ID and verify it with a small test transaction before withdrawing.' },
      { q: 'Why is my account under review?', a: 'Accounts may be reviewed if unusual activity is detected or if KYC documents need verification. This typically takes 24–48 hours. You\'ll receive an email once resolved.' },
      { q: 'Can I have multiple accounts?', a: 'No. Each person is allowed one reviewer account. Multiple accounts violate our Terms of Service and may result in permanent bans.' },
    ],
  },
  {
    id: 'campaigns',
    icon: 'MegaphoneIcon',
    color: '#1A237E',
    bg: '#EEF0FB',
    title: 'Campaigns & Reviews',
    faqs: [
      { q: 'My application was rejected — what can I do?', a: 'Brands select reviewers based on profile completeness, review history, and category match. Improve your profile, add more details about your reviewing experience, and try applying to other campaigns.' },
      { q: 'I submitted my review but it\'s not approved yet?', a: 'Review approval takes 3–7 business days. Our quality team checks authenticity, length, and platform compliance. You\'ll receive a notification once it\'s approved or if changes are needed.' },
      { q: 'What if the product I received is damaged?', a: 'Contact us immediately via this support page with photos of the damaged product. We\'ll coordinate with the brand for a replacement or alternative resolution.' },
      { q: 'Can I withdraw from a campaign after accepting?', a: 'Yes, but only before the product is shipped. Once shipped, withdrawing may affect your reviewer rating. Contact support if you have an emergency situation.' },
    ],
  },
  {
    id: 'payments',
    icon: 'BanknotesIcon',
    color: '#00BFA5',
    bg: '#E0F7F4',
    title: 'Payments & Wallet',
    faqs: [
      { q: 'My withdrawal is pending for more than 5 days?', a: 'UPI withdrawals typically process within 24 hours; bank transfers take 3–5 business days. If it\'s been longer, contact support with your transaction ID and we\'ll investigate.' },
      { q: 'Why was my withdrawal rejected?', a: 'Common reasons: incorrect UPI ID, bank account mismatch, or pending KYC verification. Check your payout details in settings and ensure your KYC is complete.' },
      { q: 'I didn\'t receive the full reward amount?', a: 'TDS (Tax Deducted at Source) is applied as per Indian tax laws. For earnings above ₹30,000/year, 10% TDS is deducted. You\'ll receive Form 16A for tax filing.' },
      { q: 'Can I transfer my wallet balance to a friend?', a: 'No. Wallet balances can only be withdrawn to your own verified bank account or UPI ID. Transfers to third parties are not supported.' },
    ],
  },
  {
    id: 'technical',
    icon: 'WrenchScrewdriverIcon',
    color: '#8B5CF6',
    bg: '#EDE9FE',
    title: 'Technical Issues',
    faqs: [
      { q: 'The app is not loading — what should I do?', a: 'Try clearing your browser cache and cookies, or use a different browser. If using the mobile app, force-close and reopen it. Check our status page for any ongoing outages.' },
      { q: 'I can\'t upload my review proof/screenshot?', a: 'Ensure your file is under 10MB and in JPG, PNG, or PDF format. If the issue persists, try a different browser or device. Contact support with the error message you see.' },
      { q: 'My OTP is not arriving?', a: 'Check that your phone number is correct in your profile. OTPs expire in 10 minutes. Try requesting a new one. If your number is correct and OTPs still don\'t arrive, contact support.' },
      { q: 'How do I report a bug or technical issue?', a: 'Use the contact form below with "Technical Issue" as the subject. Include your browser/device details, steps to reproduce the issue, and any error messages or screenshots.' },
    ],
  },
];

interface SupportFormData {
  name: string;
  email: string;
  subject: string;
  category: string;
  message: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  subject?: string;
  message?: string;
}

const SUPPORT_CATEGORIES = [
  { value: 'account', label: 'Account & Profile' },
  { value: 'campaign', label: 'Campaign Issue' },
  { value: 'payment', label: 'Payment / Withdrawal' },
  { value: 'technical', label: 'Technical Problem' },
  { value: 'other', label: 'Other' },
];

export default function SupportPageClient() {
  const [showModal, setShowModal] = useState(false);
  const [openFaq, setOpenFaq] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>('account');
  const [formData, setFormData] = useState<SupportFormData>({ name: '', email: '', subject: '', category: 'account', message: '' });
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessage, setChatMessage] = useState('');
  const [chatMessages, setChatMessages] = useState<{ role: 'user' | 'bot'; text: string }[]>([
    { role: 'bot', text: 'Hi! 👋 I\'m the IndianReviewClub support bot. How can I help you today? You can ask about campaigns, payments, account issues, or anything else.' },
  ]);
  const [chatLoading, setChatLoading] = useState(false);

  const validate = (): boolean => {
    const e: FormErrors = {};
    if (!formData.name.trim()) e.name = 'Name is required';
    if (!formData.email.trim()) e.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) e.email = 'Enter a valid email';
    if (!formData.subject.trim()) e.subject = 'Subject is required';
    if (!formData.message.trim()) e.message = 'Message is required';
    else if (formData.message.trim().length < 20) e.message = 'Message must be at least 20 characters';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
    if (errors[name as keyof FormErrors]) setErrors((p) => ({ ...p, [name]: undefined }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      const supabase = createClient();
      const { error } = await supabase.from('contact_submissions').insert({
        name: formData.name,
        email: formData.email,
        subject: `[Support] ${formData.subject}`,
        inquiry_type: formData.category,
        message: formData.message,
        source: 'support_page',
      });
      if (error) throw new Error(error.message);
      setSubmitted(true);
    } catch (err: any) {
      setSubmitError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const sendChatMessage = () => {
    if (!chatMessage.trim() || chatLoading) return;
    const userMsg = chatMessage.trim();
    setChatMessages((p) => [...p, { role: 'user', text: userMsg }]);
    setChatMessage('');
    setChatLoading(true);

    // Smart FAQ matching bot
    setTimeout(() => {
      const q = userMsg.toLowerCase();
      let reply = '';
      if (q.includes('password') || q.includes('reset')) {
        reply = 'To reset your password, go to the login page and click "Forgot Password". Enter your email and we\'ll send a reset link. Check your spam folder too!';
      } else if (q.includes('withdraw') || q.includes('payout') || q.includes('payment')) {
        reply = 'Withdrawals via UPI process within 24 hours; bank transfers take 3–5 business days. Minimum withdrawal is ₹200. Make sure your KYC is complete and your UPI ID is correct.';
      } else if (q.includes('apply') || q.includes('campaign') || q.includes('rejected')) {
        reply = 'To apply for campaigns, visit the Products page and click "Apply Now". Brands review applications within 3–5 days. If rejected, try improving your profile and applying to other campaigns.';
      } else if (q.includes('review') || q.includes('approved') || q.includes('pending')) {
        reply = 'Review approval takes 3–7 business days. Our quality team checks authenticity and platform compliance. You\'ll get a notification once approved.';
      } else if (q.includes('kyc') || q.includes('verification') || q.includes('document')) {
        reply = 'KYC requires a valid PAN card and bank account/UPI ID. The process takes 24–48 hours. Go to your dashboard → Settings → KYC to submit documents.';
      } else if (q.includes('tds') || q.includes('tax')) {
        reply = 'TDS of 10% is deducted on earnings above ₹30,000/year as per Indian tax laws. You\'ll receive Form 16A for tax filing.';
      } else {
        reply = 'I\'m not sure about that specific issue. Please use the contact form below to reach our support team — they typically respond within 24–48 hours. You can also check the FAQ sections above for common questions.';
      }
      setChatMessages((p) => [...p, { role: 'bot', text: reply }]);
      setChatLoading(false);
    }, 800);
  };

  const activeCat = FAQ_CATEGORIES.find((c) => c.id === activeCategory) || FAQ_CATEGORIES[0];

  return (
    <>
      <Header onDownloadClick={() => setShowModal(true)} />
      <main className="min-h-screen bg-white pt-16">
        {/* Hero */}
        <section className="relative overflow-hidden bg-white border-b border-gray-100 py-14 px-4">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 right-0 w-96 h-96 bg-orange-50 rounded-full blur-3xl opacity-50 translate-x-1/2 -translate-y-1/2" />
            <div className="absolute bottom-0 left-0 w-72 h-72 bg-purple-50 rounded-full blur-3xl opacity-40 -translate-x-1/3 translate-y-1/3" />
          </div>
          <div className="relative max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-orange-50 border border-orange-100 text-orange-600 text-xs font-semibold px-3.5 py-1.5 rounded-full mb-5">
              <Icon name="LifebuoyIcon" size={14} />
              Support Centre
            </div>
            <h1 className="font-heading font-bold text-3xl sm:text-4xl md:text-5xl text-gray-900 mb-4 leading-tight">
              We&apos;re here to <span className="text-primary">help you</span>
            </h1>
            <p className="text-gray-500 text-base max-w-xl mx-auto leading-relaxed">
              Find instant answers in our FAQ, submit a support ticket, or chat with our bot for quick guidance.
            </p>
            {/* Quick stats */}
            <div className="flex items-center justify-center gap-6 mt-8 flex-wrap">
              {[
                { icon: 'ClockIcon', label: '24–48h response time' },
                { icon: 'ChatBubbleLeftRightIcon', label: 'Live chat available' },
                { icon: 'CheckCircleIcon', label: '95% issues resolved' },
              ].map((s) => (
                <div key={s.label} className="flex items-center gap-2 text-sm text-gray-500">
                  <Icon name={s.icon as any} size={15} className="text-primary" />
                  {s.label}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Category tabs + FAQ */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 py-14">
          <div className="mb-8">
            <h2 className="font-heading font-bold text-2xl text-gray-900 mb-2">Frequently Asked Questions</h2>
            <p className="text-gray-500 text-sm">Browse by topic to find answers fast.</p>
          </div>

          {/* Category tabs */}
          <div className="flex gap-2 flex-wrap mb-8">
            {FAQ_CATEGORIES.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all border ${
                  activeCategory === cat.id
                    ? 'border-primary bg-primary-container text-primary' :'border-gray-200 text-gray-500 hover:border-gray-300 hover:text-gray-700 bg-white'
                }`}
              >
                <Icon name={cat.icon as any} size={15} style={{ color: activeCategory === cat.id ? cat.color : undefined }} />
                {cat.title}
              </button>
            ))}
          </div>

          {/* FAQ accordion */}
          <div className="space-y-3">
            {activeCat.faqs.map((faq, i) => {
              const key = `${activeCat.id}-${i}`;
              const open = openFaq === key;
              return (
                <div key={i} className="border border-gray-100 rounded-2xl overflow-hidden bg-white">
                  <button
                    onClick={() => setOpenFaq(open ? null : key)}
                    className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50 transition-colors"
                  >
                    <span className="font-medium text-gray-900 text-sm pr-4">{faq.q}</span>
                    <Icon
                      name={open ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                      size={16}
                      className="text-gray-400 shrink-0"
                    />
                  </button>
                  {open && (
                    <div className="px-5 pb-5">
                      <p className="text-sm text-gray-500 leading-relaxed">{faq.a}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Contact Form */}
        <section className="bg-[#F7F8FA] border-t border-gray-100 py-14 px-4">
          <div className="max-w-2xl mx-auto">
            <div className="mb-8 text-center">
              <h2 className="font-heading font-bold text-2xl text-gray-900 mb-2">Submit a Support Ticket</h2>
              <p className="text-gray-500 text-sm">Can&apos;t find your answer above? Our team will respond within 24–48 hours.</p>
            </div>

            {submitted ? (
              <div className="bg-white border border-green-100 rounded-3xl p-10 text-center shadow-sm">
                <div className="w-16 h-16 bg-green-50 rounded-2xl flex items-center justify-center mx-auto mb-5">
                  <Icon name="CheckCircleIcon" size={36} className="text-green-500" />
                </div>
                <h3 className="font-heading font-bold text-xl text-gray-900 mb-2">Ticket submitted!</h3>
                <p className="text-gray-500 text-sm mb-6 max-w-sm mx-auto">
                  Thanks <strong>{formData.name}</strong>! We&apos;ve received your request and will get back to you at <strong>{formData.email}</strong> within 24–48 hours.
                </p>
                <button
                  onClick={() => { setSubmitted(false); setFormData({ name: '', email: '', subject: '', category: 'account', message: '' }); }}
                  className="inline-flex items-center gap-2 text-sm font-semibold text-primary border border-primary/20 bg-orange-50 px-5 py-2.5 rounded-xl hover:bg-orange-100 transition-colors"
                >
                  <Icon name="ArrowPathIcon" size={16} />
                  Submit another ticket
                </button>
              </div>
            ) : (
              <div className="bg-white border border-gray-100 rounded-3xl p-6 sm:p-8 shadow-sm">
                <form onSubmit={handleSubmit} noValidate className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Full Name <span className="text-primary">*</span></label>
                      <input
                        type="text" name="name" value={formData.name} onChange={handleChange}
                        placeholder="Rahul Sharma"
                        className={`w-full px-4 py-2.5 rounded-xl border text-sm outline-none transition-colors focus:ring-2 focus:ring-primary/20 focus:border-primary ${errors.name ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50 hover:border-gray-300'}`}
                      />
                      {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Email Address <span className="text-primary">*</span></label>
                      <input
                        type="email" name="email" value={formData.email} onChange={handleChange}
                        placeholder="rahul@example.com"
                        className={`w-full px-4 py-2.5 rounded-xl border text-sm outline-none transition-colors focus:ring-2 focus:ring-primary/20 focus:border-primary ${errors.email ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50 hover:border-gray-300'}`}
                      />
                      {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email}</p>}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Category</label>
                      <select
                        name="category" value={formData.category} onChange={handleChange}
                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      >
                        {SUPPORT_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Subject <span className="text-primary">*</span></label>
                      <input
                        type="text" name="subject" value={formData.subject} onChange={handleChange}
                        placeholder="Brief description of your issue"
                        className={`w-full px-4 py-2.5 rounded-xl border text-sm outline-none transition-colors focus:ring-2 focus:ring-primary/20 focus:border-primary ${errors.subject ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50 hover:border-gray-300'}`}
                      />
                      {errors.subject && <p className="mt-1 text-xs text-red-500">{errors.subject}</p>}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Message <span className="text-primary">*</span></label>
                    <textarea
                      name="message" value={formData.message} onChange={handleChange} rows={5}
                      placeholder="Describe your issue in detail. Include any error messages, screenshots, or steps to reproduce the problem."
                      className={`w-full px-4 py-2.5 rounded-xl border text-sm outline-none transition-colors focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none ${errors.message ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50 hover:border-gray-300'}`}
                    />
                    {errors.message && <p className="mt-1 text-xs text-red-500">{errors.message}</p>}
                  </div>
                  {submitError && (
                    <div className="flex items-center gap-2 bg-red-50 border border-red-100 rounded-xl px-4 py-3 text-sm text-red-600">
                      <Icon name="ExclamationCircleIcon" size={16} />
                      {submitError}
                    </div>
                  )}
                  <button
                    type="submit" disabled={submitting}
                    className="w-full cta-gradient text-white font-semibold py-3 rounded-xl hover:opacity-90 transition-opacity disabled:opacity-60 flex items-center justify-center gap-2"
                  >
                    {submitting ? (
                      <>
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                        </svg>
                        Submitting…
                      </>
                    ) : (
                      <>
                        <Icon name="PaperAirplaneIcon" size={16} />
                        Submit Support Ticket
                      </>
                    )}
                  </button>
                </form>
              </div>
            )}
          </div>
        </section>

        {/* Other help links */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { icon: 'QuestionMarkCircleIcon', color: '#FF5722', bg: '#FFF3EE', title: 'Help Centre', desc: 'Browse all FAQs and guides', href: '/help' },
              { icon: 'EnvelopeIcon', color: '#1A237E', bg: '#EEF0FB', title: 'Email Support', desc: 'hello@indianreviewclub.com', href: 'mailto:hello@indianreviewclub.com' },
              { icon: 'ChatBubbleLeftRightIcon', color: '#00BFA5', bg: '#E0F7F4', title: 'General Contact', desc: 'Partnerships & press inquiries', href: '/contact' },
            ].map((item) => (
              <Link
                key={item.title}
                href={item.href}
                className="group bg-white border border-gray-100 rounded-2xl p-5 hover:shadow-md transition-all hover:-translate-y-0.5"
              >
                <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: item.bg }}>
                  <Icon name={item.icon as any} size={20} style={{ color: item.color }} />
                </div>
                <p className="font-semibold text-gray-900 text-sm mb-1 group-hover:text-primary transition-colors">{item.title}</p>
                <p className="text-xs text-gray-400">{item.desc}</p>
              </Link>
            ))}
          </div>
        </section>
      </main>
      <Footer />

      {/* Live Chat Widget */}
      <div className="fixed bottom-6 right-6 z-50">
        {chatOpen && (
          <div className="mb-3 w-80 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden flex flex-col" style={{ height: 420 }}>
            {/* Chat header */}
            <div className="bg-primary px-4 py-3 flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                <Icon name="ChatBubbleLeftRightIcon" size={16} className="text-white" />
              </div>
              <div className="flex-1">
                <div className="text-white font-semibold text-sm">Support Chat</div>
                <div className="flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-300" />
                  <span className="text-white/70 text-xs">Bot online</span>
                </div>
              </div>
              <button onClick={() => setChatOpen(false)} className="text-white/70 hover:text-white p-1">
                <Icon name="XMarkIcon" size={16} />
              </button>
            </div>
            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
              {chatMessages.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${
                    msg.role === 'user' ?'bg-primary text-white rounded-br-sm' :'bg-white text-gray-700 border border-gray-100 rounded-bl-sm shadow-sm'
                  }`}>
                    {msg.text}
                  </div>
                </div>
              ))}
              {chatLoading && (
                <div className="flex justify-start">
                  <div className="bg-white border border-gray-100 rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm">
                    <div className="flex gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </div>
              )}
            </div>
            {/* Input */}
            <div className="p-3 border-t border-gray-100 bg-white flex gap-2">
              <input
                type="text"
                value={chatMessage}
                onChange={(e) => setChatMessage(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') sendChatMessage(); }}
                placeholder="Type your question…"
                className="flex-1 px-3 py-2 rounded-xl border border-gray-200 text-sm outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary bg-gray-50"
              />
              <button
                onClick={sendChatMessage}
                disabled={!chatMessage.trim() || chatLoading}
                className="w-9 h-9 rounded-xl bg-primary text-white flex items-center justify-center hover:opacity-90 transition-opacity disabled:opacity-40"
              >
                <Icon name="PaperAirplaneIcon" size={15} />
              </button>
            </div>
          </div>
        )}
        <button
          onClick={() => setChatOpen((p) => !p)}
          className="w-14 h-14 rounded-full bg-primary text-white shadow-lg flex items-center justify-center hover:opacity-90 transition-all hover:scale-105"
          aria-label="Open support chat"
        >
          {chatOpen
            ? <Icon name="XMarkIcon" size={22} />
            : <Icon name="ChatBubbleLeftRightIcon" size={22} />
          }
        </button>
      </div>

      {showModal && <AppDownloadModal onClose={() => setShowModal(false)} />}
    </>
  );
}
